import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  ImageBackground,
  Keyboard,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import ButtonComponent from '../../../components/ButtonComponent/ButtonComponent';
import { Colors } from '../../../constants/Colors';
import { Images } from '../../../constants/Images';
import AuthService from '../../../services/AuthService/AuthService';
import { styles } from './styles';
import { NavigationProps } from '../../../navigation/Routes';
import { AppRoutes } from '../../../navigation/Routes.types';
import { useLoadingStore } from '../../../store/LoadingStore';
import { useRegisterFinishStore } from '../../../store/RegisterStore';
import { showSuccess, showInfo } from '../../../utils/globalAlert';

const TokenScreen = () => {
  const { navigate, goBack } = useNavigation<NavigationProps>();

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [resendChannel, setResendChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [resendingChannel, setResendingChannel] = useState<'whatsapp' | 'email' | null>(null);
  const { hideLoading, showLoading } = useLoadingStore();
  const { registerFinishData } = useRegisterFinishStore()

  const inputRefs = [
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
    useRef<TextInput>(null),
  ];

  useEffect(() => setTimer(60), [])
  useEffect(() => {
    const startTimer = timer > 0 && setInterval(() => {
      setTimer(timer - 1)
    }, 1000);

    return () => {
      startTimer && clearInterval(startTimer)
    }
  }, [timer])
  const formatTimer = () => {
    if(timer == 60) return '1:00'
    const str = `${timer}`.padStart(2, '0')
    return `0:${str}`
  }

  const maskPhone = (phone?: string) => {
    if (!phone) {
      return '';
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) {
      return '****';
    }
    const ddd = digits.slice(0, 2);
    const last4 = digits.slice(-4);
    return `(${ddd}) ****-${last4}`;
  };

  const maskEmail = (email?: string) => {
    if (!email) {
      return '';
    }
    const [user, domain] = email.split('@');
    if (!domain) {
      return '****';
    }
    const visible = user.slice(0, 3);
    const maskedUser = user.length > 3 ? `${visible}***` : visible;
    return `${maskedUser}@${domain}`;
  };

  const handleResendToken = async (channel: 'whatsapp' | 'email') => {
    showLoading()
    setResendingChannel(channel);
    try {
      const response = await AuthService.resendTokenPost(registerFinishData.cpfCnpj, channel)
      const retryAfter = response?.retry_after_seconds ?? 60
      setTimer(retryAfter)
      setResendChannel(channel)

      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/7dd560ae-ee35-4294-995f-b722aed46619',{
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'X-Debug-Session-Id':'12ff92',
        },
        body:JSON.stringify({
          sessionId:'12ff92',
          runId:'e2e',
          hypothesisId:'H9',
          location:'TokenScreen.tsx:handleResendToken',
          message:'Reenvio de token bem-sucedido',
          data:{channel,retryAfter},
          timestamp:Date.now(),
        }),
      }).catch(()=>{});
      // #endregion

      const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'
      showSuccess('Conecta Brasal', `Token reenviado! Confira o ${channelLabel}.`)
    } catch(error: any) {
      if (error.response && error.response.data) {
        const errorMessage =
          error.response.data.details ||
          error.response.data.message ||
          'Erro desconhecido';
        Alert.alert('Opss...', String(errorMessage));
      } else {
        Alert.alert('Opss...', 'Algo deu errado, por favor tente novamente.');
      }
    } finally {
      setResendingChannel(null);
      hideLoading()
    }
  }

  const handleInputChange = (text: string, index: number) => {
    const newCode = [...code];

    // Verifica se o texto colado tem 6 dígitos, preenchendo todos os inputs
    if (text.length === 6) {
      text.split('').forEach((digit, i) => {
        if (i < newCode.length) newCode[i] = digit;
      });
      setCode(newCode);
      inputRefs[5].current?.focus();
      return;
    }

    // Preenche apenas o campo atual se for um único caractere
    if (text.length === 1) {
      newCode[index] = text;
      setCode(newCode);
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace') {
      if (code[index] !== '') {
        // Clear the current input field
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      } else if (index > 0) {
        // Move the focus to the previous input field and clear its value
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const validateEmailToken = async () => {
    const token = code.join('');
    if (token.length < 6 || code.some(d => d === '')) {
      showInfo('Atenção', 'Por favor, preencha todos os 6 dígitos do código.');
      return;
    }
    showLoading();
    try {
      const response = await AuthService.validateEmailTokenPost(token);
      
      navigate(AppRoutes.VALIDATED_TOKEN);
    } catch (error: any) {
      if (error?.response && error.response.data) {
        const errorMessage =
          error.response.data.details ||
          error.response.data.message ||
          'Erro desconhecido';
        Alert.alert('Opss...', errorMessage);
      } else {
        Alert.alert('Opss...', 'Algo deu errado, por favor tente novamente.');
      }
      console.error(error);
    }
    hideLoading();
  };

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <ImageBackground
        // source={Images.backgroundCoke}
        style={styles.imageBackground}>
          <View style={styles.container}>
            <TouchableOpacity onPress={goBack} style={styles.arrowButton}>
              <FontAwesome6
                name="arrow-left"
                color={Colors.GrayPallete.lightgrayThree}
                size={22}
              />
            </TouchableOpacity>
            <Text style={styles.title}>Validação de Token</Text>
            <Text style={styles.description}>
              Enviamos um código de 6 dígitos para o seu{' '}
              {resendChannel === 'email' ? 'E-mail' : 'WhatsApp'}. Ele é válido
              por 30 minutos. Digite abaixo para finalizar o seu cadastro.
            </Text>
            {resendChannel === 'whatsapp' && (
              <Text
                style={{
                  width: '100%',
                  textAlign: 'center',
                  marginTop: 8,
                  marginBottom: 12,
                  fontFamily: 'TCCC-UnityHeadline Regular',
                }}>
                WhatsApp: {maskPhone(registerFinishData.phone)}
              </Text>
            )}
            {resendChannel === 'email' && (
              <Text
                style={{
                  width: '100%',
                  textAlign: 'center',
                  marginTop: 8,
                  marginBottom: 12,
                  fontFamily: 'TCCC-UnityHeadline Regular',
                }}>
                E-mail: {maskEmail(registerFinishData.email)}
              </Text>
            )}
            <View style={styles.containerBox}>
              {code.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={inputRefs[index]}
                  style={styles.containerTextBox}
                  placeholder="*"
                  maxLength={1}
                  placeholderTextColor={'lightgray'}
                  keyboardType="numeric"
                  value={digit}
                  onChangeText={(text) => handleInputChange(text, index)}
                  onKeyPress={(e) => handleKeyPress(e, index)}
                />
              ))}
            </View>
            
            <Text
              style={{
                width: '100%',
                textAlign: 'center',
                fontFamily: 'TCCC-UnityHeadline Regular',
              }}>
              {timer > 0
                ? `Você poderá solicitar um novo código em ${formatTimer()}.`
                : 'Você já pode solicitar um novo código.'}
            </Text>
                        
            <ButtonComponent title="Validar" onPress={validateEmailToken} />

            {timer === 0 && (
              <View style={{ marginTop: 20, width: '100%' }}>
                <Text
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    marginBottom: 12,
                    fontFamily: 'TCCC-UnityHeadline Regular',
                  }}>
                  Não recebeu? Escolha como deseja receber o novo código:
                </Text>
                <ButtonComponent
                  title={resendingChannel === 'whatsapp' ? 'Enviando...' : 'Reenviar por WhatsApp'}
                  iconName="whatsapp"
                  onPress={() => handleResendToken('whatsapp')}
                  disabled={!!resendingChannel}
                  isLoading={resendingChannel === 'whatsapp'}
                />
                <View style={{ height: 12 }} />
                <ButtonComponent
                  title={resendingChannel === 'email' ? 'Enviando...' : 'Reenviar por E-mail'}
                  iconName="envelope"
                  onPress={() => handleResendToken('email')}
                  disabled={!!resendingChannel}
                  isLoading={resendingChannel === 'email'}
                />
              </View>
            )}
          </View>
      </ImageBackground>
    </TouchableWithoutFeedback>
  );
};

export default TokenScreen;
